import React from 'react';

/**
 * Button Component
 *
 * A reusable button with consistent styling across the application.
 * Uses design tokens from tailwind.config.js (brand, risk-high, neutral).
 *
 * @example
 * // Primary button (default)
 * <Button onClick={handleSave}>Save</Button>
 *
 * // Secondary outline button
 * <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
 *
 * // Danger button for destructive actions
 * <Button variant="danger" onClick={handleDelete}>Delete</Button>
 *
 * // Ghost button (minimal)
 * <Button variant="ghost" size="sm">Edit</Button>
 *
 * // With icon
 * <Button icon={<MdAdd />}>Add Rule</Button>
 *
 * // Loading state
 * <Button isLoading>Saving...</Button>
 */

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled = false,
  icon = null,
  className = '',
  type = 'button',
  ...props
}) => {
  // Base styles applied to all buttons
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-medium rounded-lg
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-1
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  // Variant styles using design tokens
  const variants = {
    primary: `
      text-white
      bg-gradient-to-r from-brand-600 to-blue-600
      hover:from-brand-700 hover:to-blue-700
      focus:ring-brand-500/30
      shadow-sm hover:shadow-md
    `,
    secondary: `
      text-neutral-700
      bg-white
      border border-neutral-300
      hover:bg-neutral-50 hover:border-neutral-400
      focus:ring-neutral-500/20
      hover:shadow-sm
    `,
    danger: `
      text-white
      bg-risk-high-600
      hover:bg-risk-high-700
      focus:ring-risk-high-500/30
      shadow-sm hover:shadow-md
    `,
    ghost: `
      text-neutral-600
      bg-transparent
      hover:bg-neutral-100
      focus:ring-neutral-500/20
    `,
  };

  // Size styles
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-sm',
  };

  // Loading spinner component
  const Spinner = () => (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <Spinner />
          {children}
        </>
      ) : (
        <>
          {icon && <span className="flex-shrink-0">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default Button;
